/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module GslbModule */

import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from '@angular/core';

import { createDropdownOption } from 'ng/shared/utils/dropdown.utils';
import { GSLBService } from 'ajs/modules/gslb/factories/gslb-service/gslb-service.item.factory';
import { GSLBVSCollection } from 'collections/gslb-vs.collection.factory';
import { GslbPoolMemberConfigItem } from 'message-items/gslb-pool-member.config-item.factory';
import { IAviDropdownOption } from 'standalone-components/avi-dropdown/avi-dropdown.types';
import { StringService } from 'string-service';
import * as globalL10n from 'global-l10n';

const { ...globalL10nKeys } = globalL10n;

/**
 * @description
 *     GSLB service pool member type component.
 *     Wrapper to display configuration either by IP address or virtual service.
 * @author Suraj Kumar, Umesh Chovatiya, alextsg
 */
@Component({
    selector: 'gslb-pool-member-type',
    templateUrl: './gslb-pool-member-type.component.html',
})
export class GslbPoolMemberTypeComponent implements OnInit {
    @Input()
    public editable: GslbPoolMemberConfigItem;

    @Input()
    public gslbService: GSLBService;

    /**
     * Flag indicating pool member configuration type.
     * We have 2 types IP address and virtual service.
     */
    @Input()
    public isConfiguredByTypeIp = false;

    /**
     * Check whether the GSLB service type is SRV or not.
     */
    @Input()
    public isGSLBServiceRecordTypeSRV = false;

    /**
     * Emit true, if pool member configuration is set by IP address type.
     * False, if configured by Virtual Service.
     */
    @Output()
    public isConfiguredByTypeIpChange = new EventEmitter<boolean>();

    /**
     * Emit an event when the user selects or changes the VS or FQDN/IP.
     */
    @Output()
    public onAddressChange = new EventEmitter<string>();

    /**
     * List of GslbSite ids.
     */
    public gslbSites: string[] = [];

    public resolvedVsVipIpDropdownOptions: IAviDropdownOption[] = [];

    public siteClusterControllerDropdownOptions: IAviDropdownOption[] = [];

    /**
     * Reference for VS collection of selected site.
     */
    public selectedSiteVsCollection: GSLBVSCollection;

    public readonly globalL10nKeys = globalL10nKeys;

    constructor(private readonly stringService: StringService) {}

    /** @override */
    public async ngOnInit(): Promise<void> {
        await this.setInitialSettingsByVSType();
    }

    /**
     * Handle site cluster controller selection change.
     */
    public handleSiteClusterControllerChange(): void {
        this.resolvedVsVipIpDropdownOptions = [];

        this.editable.handleSiteClusterControllerChange();

        this.selectedSiteVsCollection = this.editable.getSelectedSiteVsCollection(this.gslbSites);
    }

    /**
     * Handle VS selection change.
     */
    public async handleVsSelectionChange(): Promise<void> {
        const vsIps = await this.editable.onVsSelectionChange(this.selectedSiteVsCollection);

        this.resolvedVsVipIpDropdownOptions = this.createDropdownOptions(vsIps);
        this.handleAddressChange();
    }

    /**
     * Emit an event when the user selects or changes the VS or FQDN/IP.
     */
    public handleAddressChange(): void {
        this.onAddressChange.emit(this.editable.config.ip.address);
    }

    /**
     * Handle pool memeber configuration type change.
     */
    public handleMemberConfigurationTypeChange(): void {
        this.editable.resetSettingForConfigurationType(this.isConfiguredByTypeIp);
        this.isConfiguredByTypeIpChange.emit(this.isConfiguredByTypeIp);
        this.handleAddressChange();
    }

    /**
     * Set initial configuration for pool member configured by type virtual service.
     */
    private async setInitialSettingsByVSType(): Promise<void> {
        const vsIps = await this.editable.getPoolMemberVsData();

        this.resolvedVsVipIpDropdownOptions = this.createDropdownOptions(vsIps);
        this.gslbSites = this.gslbService.getGSLB()?.getSites() || [];

        setTimeout(() => this.gslbService.setPristine());

        this.siteClusterControllerDropdownOptions = this.createSiteClusterControllerOptions();
        this.selectedSiteVsCollection = this.editable.getSelectedSiteVsCollection(this.gslbSites);
    }

    /**
     * Create dropdown options.
     */
    private createDropdownOptions(entries: string[] = []): IAviDropdownOption[] {
        return entries.map((ip: string) => createDropdownOption(ip));
    }

    /**
     * Create site cluster controller dropdown options.
     */
    private createSiteClusterControllerOptions(): IAviDropdownOption[] {
        return this.gslbSites.map((site: string) => createDropdownOption(
            this.stringService.slug(site),
            this.stringService.name(site),
        )) || [];
    }
}
